import React, { useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid, Box, CssBaseline } from "@mui/material";

import {
  mainLayoutStyles,
  showNotification,
  images,
} from "../../../../../constants";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";

import ImagePreviewModal from "../../../../../common/ImagePreviewModal";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import GAReactionDetailsDataFieldsSection from "../../components/GAReactionDetailsDataFieldsSection/GAReactionDetailsDataFieldsSection";
import { useAuth } from "../../../../../hooks";
import { updateReaction } from "../../../../../modules/OrganizationReactions/hooks/ReactionsHooks";
import { useReactionsContext } from "../../../../../providers/OrganizationAdmin/ReactionsProvider";
import { Toast } from "../../../../../common/alert";
import AudioModal from "../../../../../common/AudioModal";
import ConfirmationModal from "../../../../../common/ConfirmationModal";
import { uploadReactionAudioToStorage } from "../../hooks/GAReactionsHooks";
import { getUserContentReports } from "../../../GAContentModeration/hooks/ContentReportsHooks";
import { getSingleUser } from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import { ContentModerationTable } from "../../../GAContentModeration/components/components";
import Scrollbars from "react-custom-scrollbars-2";
import { getAuth } from "firebase/auth";

const GAReactionDetailsContainer = () => {
  const { currentUser } = useAuth();
  const { handleGetAllGAReactions, setLoading, loading } =
    useReactionsContext();
  const scrollbarsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [reactionDetails, setReactionDetails] = useState(
    location?.state?.reactionDetails || {}
  );
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [uploadProcessedAudioFile, setUploadProcessedAudioFile] = useState(null);
  const [processedAudioModal, setProcessedAudioModal] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [reactionsReports, setReactionsReports] = useState([]);

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleProcessedAudioModalOpen = () => {
    setProcessedAudioModal(true);
  };
  const handleProcessedAudioModalClose = () => {
    setProcessedAudioModal(false);
  };

  // --- Audio upload & process handlers (modeled after GAQuickTaleDetailsContainer) ---

  const handleProcessOriginalAudio = () => {
    setConfirmationModalOpen(true);
  };

  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
    // User chose not to process the audio
    setReactionDetails((prev) => ({
      ...prev,
      userNoProcess: true,
    }));
  };

  /**
   * Calls processAudioWithAuphonicV5 (HTTP onRequest function) to clean &
   * normalize the reaction's original audio.
   */
  const handleProcessAudio = async () => {
    setConfirmationModalOpen(false);
    if (!reactionDetails.audioUrl) {
      Toast("error", "No original audio file to process.");
      return;
    }
    setLoading(true);
    try {
      // Get Firebase ID token for authorization
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken(true);

      // Build the Cloud Function URL
      const projectId = auth.app.options.projectId;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/processAudioWithAuphonicV5`;

      const qtTitle = `Reaction to ${reactionDetails?.quicktaleData?.title || "Untitled"}`;

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fileUrl: reactionDetails.audioUrl,
          qtTitle: qtTitle,
          ogFileName: reactionDetails.originalAudioName || "reaction_audio",
          collectionName: "quicktaleReaction",
          documentId: reactionDetails.id,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Create audio element to get duration of processed file
        const audio = new Audio(result.filePath);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration);
            setReactionDetails((prev) => ({
              ...prev,
              processedAudioUrl: result.filePath,
              processedAudioName: result.processedAudioName,
              audioLength: duration,
              userNoProcess: false,
            }));
            resolve();
          };
          // In case metadata doesn't load (e.g., CORS), resolve after timeout
          setTimeout(() => {
            setReactionDetails((prev) => ({
              ...prev,
              processedAudioUrl: result.filePath,
              processedAudioName: result.processedAudioName,
              userNoProcess: false,
            }));
            resolve();
          }, 5000);
        });
        showNotification("Audio successfully processed and updated.");
      } else {
        throw new Error(result.message || "Failed to process audio.");
      }
    } catch (error) {
      Toast("error", `Error processing audio: ${error.message}`);
      console.error("Error calling processAudioWithAuphonicV5:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles immediate upload of audio files to Firebase Storage.
   * Called when user uploads a file or records audio via the mic.
   */
  const handleFileUpload = async (file, type) => {
    if (!file) return;

    setLoading(true);
    try {
      if (type === "original") {
        const audioUrl = await uploadReactionAudioToStorage(file.raw);
        const dataTemp = {
          audioUrl: audioUrl,
          originalAudioName: file.raw.name,
        };

        // Create an audio element to get the duration
        const audio = new Audio(audioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration);
            dataTemp.audioLength = duration;
            setReactionDetails((prev) => ({
              ...prev,
              ...dataTemp,
              audioLength: duration,
            }));
            resolve();
          };
          // Fallback timeout in case metadata doesn't load
          setTimeout(() => {
            setReactionDetails((prev) => ({
              ...prev,
              ...dataTemp,
            }));
            resolve();
          }, 5000);
        });

        // Prompt user to process the audio
        setConfirmationModalOpen(true);
      }
      // Note: "processed" type not needed since hideUploadButton={true} for processed audio
    } catch (error) {
      Toast("error", `Error uploading file: ${error.message}`);
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have no data - redirect to parent page
    if (!location?.state?.reactionDetails?.id) {
      navigate('/reactions', { replace: true });
      return;
    }

    setReactionDetails(location.state.reactionDetails);
    handleGetReactionReports();
  }, [location.state, navigate]);

  // Don't render anything if we don't have valid reaction data
  if (!reactionDetails?.id) {
    return null;
  }

  const handleGetReactionReports = async () => {
    try {
      let querySnapshot = await getUserContentReports(
        location.state.reactionDetails.id
      );
      const reportsPromises = querySnapshot.docs.map(async (item) => {
        const userTemp = await getSingleUser(item.data().reportedBy);
        return {
          ...item.data(),
          id: item.id,
          reportedByData: userTemp?.data() ? userTemp.data() : {},
        };
      });

      const contentReports = await Promise.all(reportsPromises);

      console.log("reports:", contentReports);

      setReactionsReports(contentReports);
    } catch (error) {
      console.log("error getting user groups:", error.message);
    }
  };

  const handleUpdateReaction = async () => {
    try {
      setLoading(true);
      let dataTemp = {
        status: reactionDetails?.status ?? "",
        adminNotes: reactionDetails?.adminNotes ?? "",
        displayName: reactionDetails?.displayName ?? "",
        user: reactionDetails?.user ?? "",
        updatedAt: new Date(),
        updatedBy: currentUser?.id ?? "",
      };

      // Include audio fields from state (handleFileUpload already uploaded to Storage)
      if (reactionDetails.audioUrl) {
        dataTemp.audioUrl = reactionDetails.audioUrl;
      }
      if (reactionDetails.originalAudioName) {
        dataTemp.originalAudioName = reactionDetails.originalAudioName;
      }
      if (reactionDetails.audioLength) {
        dataTemp.audioLength = reactionDetails.audioLength;
      }
      if (reactionDetails.processedAudioUrl) {
        dataTemp.processedAudioUrl = reactionDetails.processedAudioUrl;
      } else {
        dataTemp.processedAudioUrl = '';
      }
      if (reactionDetails.processedAudioName) {
        dataTemp.processedAudioName = reactionDetails.processedAudioName;
      } else {
        dataTemp.processedAudioName = '';
      }
      if (reactionDetails.userNoProcess !== undefined) {
        dataTemp.userNoProcess = reactionDetails.userNoProcess;
      }
      
      // Update the database
      let responseTemp = await updateReaction(reactionDetails?.id, dataTemp);
      console.log("update reaction response:", responseTemp);
      
      // Update local state immediately after successful database update
      setReactionDetails(prevDetails => ({
        ...prevDetails,
        ...dataTemp,
        updatedAt: dataTemp.updatedAt
      }));
      
      // Clear upload files after successful update
      setUploadAudioFile(null);
      setUploadProcessedAudioFile(null);
      
      // Try to refresh global data, but don't let it block the UI update
      try {
        await handleGetAllGAReactions();
      } catch (refreshError) {
        console.log("Warning: Failed to refresh global reactions data:", refreshError.message);
        // Don't show error to user since the main update succeeded
      }
      
      showNotification("Reaction updated successfully.");
      setEditEnabled(false);
      
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating reaction", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={"Reaction Details"}
        bgColor="#fff"
        buttonText={currentUser?.role === "moderator" ? undefined : "Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        handleSave={() => handleUpdateReaction()}
        loading={loading}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/*Data card/container section*/}
          <GAReactionDetailsDataFieldsSection
            reactionDetails={reactionDetails}
            setReactionDetails={setReactionDetails}
            handleImagePreviewModalOpen={handleImagePreviewModalOpen}
            handleAudioModalOpen={handleAudioModalOpen}
            handleProcessedAudioModalOpen={handleProcessedAudioModalOpen}
            editEnabled={editEnabled}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            uploadProcessedAudioFile={uploadProcessedAudioFile}
            setUploadProcessedAudioFile={setUploadProcessedAudioFile}
            handleFileUpload={handleFileUpload}
            onProcessOriginalAudio={handleProcessOriginalAudio}
          />
          {reactionsReports?.length ? (
            <Scrollbars
              style={scrollbar}
              autoHide={true}
              ref={scrollbarsRef} // Assign the ref to Scrollbars instance
              renderThumbVertical={({ style, ...props }) => (
                <div
                  {...props}
                  style={{
                    ...style,
                    ...scrollbarprops,
                  }}
                />
              )}
            >
              <ContentModerationTable
                selected={selected}
                setSelected={setSelected}
                contentReportsData={reactionsReports}
                userDetailsPageReports={true}
                detailsPageReports={true}
              />
            </Scrollbars>
          ) : undefined}
        </Grid>
      </Grid>
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : reactionDetails?.quicktaleData?.imageUrls[0]
            ? reactionDetails?.quicktaleData?.imageUrls[0]
            : images.QuicktaleLogo
        }
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : reactionDetails?.audioUrl
        }
      />
      <AudioModal
        modalOpen={processedAudioModal}
        handleClose={handleProcessedAudioModalClose}
        audioFile={
          uploadProcessedAudioFile != null && uploadProcessedAudioFile.preview
            ? uploadProcessedAudioFile.preview
            : reactionDetails?.processedAudioUrl
        }
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={handleConfirmationModalClose}
        onConfirm={handleProcessAudio}
        title="Process Audio"
        message="Would you like to clean and normalize audio?"
      />
    </Box>
  );
};

export default GAReactionDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};

const scrollbar = { width: "100%", height: "300px", marginLeft: "auto" };
const scrollbarprops = {
  width: "4px",
  backgroundColor: "#00A7CD",
  borderRadius: "2px",
};
